/* Company-chat compliance policy banner.
 *
 * Fires once per user (before ack), and re-openable from Profile menu via
 * `window.dispatchEvent(new CustomEvent("open-compliance-banner"))`.
 * Ack is persisted in `user_ui_preferences.chat_compliance_ack_at`.
 */
import React, { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { ShieldCheck, Eye } from "lucide-react";
import api from "@/lib/api";

export default function ChatCompliancePolicy({ user }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!user || user.role === "CUSTOMER") return;
    let cancel = false;
    api.get("/me/ui-preferences").then(({ data }) => {
      if (cancel) return;
      if (!data?.chat_compliance_ack_at) setOpen(true);
    }).catch(() => {});
    return () => { cancel = true; };
  }, [user]);

  useEffect(() => {
    const listener = () => setOpen(true);
    window.addEventListener("open-compliance-banner", listener);
    return () => window.removeEventListener("open-compliance-banner", listener);
  }, []);

  const acknowledge = async () => {
    setBusy(true);
    try {
      await api.put("/me/ui-preferences", {
        chat_compliance_ack_at: new Date().toISOString(),
      });
      setOpen(false);
    } catch (_e) {
      setOpen(false);
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && setOpen(false)}>
      <DialogContent className="max-w-md bg-[var(--surface-elevated)] border-white/10" data-testid="chat-compliance-banner">
        <DialogHeader>
          <div className="flex items-start gap-3">
            <div className="h-10 w-10 rounded-xl bg-gradient-primary/20 border border-sky-500/30 flex items-center justify-center shrink-0">
              <ShieldCheck className="h-5 w-5 text-sky-300" />
            </div>
            <DialogTitle className="font-heading tracking-[0.14em] uppercase text-sm leading-tight pt-1">
              Company Chat Policy
            </DialogTitle>
          </div>
        </DialogHeader>
        <div className="space-y-3 text-xs text-foreground/80 leading-relaxed">
          <p>
            Sharda Power chat is a work communication tool. All conversations - DMs, groups, and
            channels - are stored on company infrastructure and are subject to review by
            leadership for compliance, security, and legal purposes.
          </p>
          <div className="rounded-lg border border-sky-500/20 bg-sky-500/[0.06] p-3 space-y-1.5">
            <div className="flex items-center gap-1.5 text-sky-300 font-semibold text-[11px] uppercase tracking-widest">
              <Eye className="h-3 w-3" /> What this means
            </div>
            <ul className="space-y-1 text-[11px] text-foreground/75 list-disc pl-4">
              <li>Chat content is encrypted at rest, but the company can decrypt for compliance review.</li>
              <li>Deleted messages remain retrievable for a limited period.</li>
              <li>Every leadership review is written to an audit log.</li>
              <li>Use company chat for work topics; personal matters should stay on personal devices.</li>
            </ul>
          </div>
          <p className="text-foreground/60">
            By continuing to use company chat you acknowledge this policy. You can re-read it any
            time from your profile menu.
          </p>
        </div>
        <DialogFooter>
          <Button
            onClick={acknowledge}
            disabled={busy}
            className="bg-gradient-primary text-white"
            data-testid="chat-compliance-ack"
          >
            I understand and agree
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
